import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

export const usePushNotifications = () => {
  const { user } = useAuth();
  const [isSupported, setIsSupported] = useState(false);
  const [isEnabled, setIsEnabled] = useState(false);
  const [permission, setPermission] = useState<NotificationPermission>('default');

  useEffect(() => {
    const supported = 'Notification' in window && 'serviceWorker' in navigator;
    setIsSupported(supported);
    
    if (supported) {
      setPermission(Notification.permission);
      setIsEnabled(Notification.permission === 'granted');
      
      // Load saved preference
      const savedPreference = localStorage.getItem('push_notifications_enabled');
      if (savedPreference !== null) {
        setIsEnabled(savedPreference === 'true' && Notification.permission === 'granted');
      }
    }
  }, []);

  const requestPermission = useCallback(async () => {
    if (!isSupported) {
      toast.error('Push notifications are not supported in this browser');
      return false;
    }

    try {
      const result = await Notification.requestPermission();
      setPermission(result);
      
      if (result === 'granted') {
        setIsEnabled(true);
        localStorage.setItem('push_notifications_enabled', 'true');
        toast.success('Push notifications enabled');
        return true;
      } else if (result === 'denied') {
        setIsEnabled(false);
        localStorage.setItem('push_notifications_enabled', 'false');
        toast.error('Push notifications were denied');
        return false;
      }
      return false;
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      return false;
    }
  }, [isSupported]);

  const toggleNotifications = useCallback(async (enabled: boolean) => {
    if (enabled) {
      if (permission !== 'granted') {
        const granted = await requestPermission();
        return granted;
      }
      setIsEnabled(true);
      localStorage.setItem('push_notifications_enabled', 'true');
      toast.success('Push notifications enabled');
      return true;
    } else {
      setIsEnabled(false);
      localStorage.setItem('push_notifications_enabled', 'false');
      toast.success('Push notifications disabled');
      return true;
    }
  }, [permission, requestPermission]);

  const sendNotification = useCallback((title: string, options?: NotificationOptions) => {
    if (!isEnabled || permission !== 'granted') return;
    
    try {
      const notification = new Notification(title, {
        icon: '/favicon.ico',
        badge: '/favicon.ico',
        ...options
      });
      
      notification.onclick = () => {
        window.focus();
        notification.close();
      };
    } catch (error) {
      console.error('Error sending notification:', error);
    }
  }, [isEnabled, permission]);

  return {
    isSupported,
    isEnabled,
    permission,
    requestPermission,
    toggleNotifications,
    sendNotification
  };
};
